import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as yup from 'yup';

import BaseErrors from '@components/fields/BaseErrors';
import FormButtons from '@components/fields/FormButtons';
import Select from '@components/fields/Select';
import Form from '@components/forms/Form';
import {
  ArticleFragment as ArticleType,
  CareProfileActionableFieldEnum,
  CareProfileActionableFieldValueEnum,
  CareProfileConcernEnum,
  CareProfileNeedsFromGrayceEnum,
  ClientRoleProviderEmotionalStateEnum,
  ClientRoleProviderFeelsSupportedEnum,
  ClientRoleRelationshipEnum,
  ConditionalContentTypeEnum,
  ContentConditionCreateDocument,
  ContentConditionCreateMutation,
  ContentConditionCreateMutationVariables,
  ContentConditionKindEnum,
  GoalTemplateFragment as GoalTemplateType,
  StageEnum
} from '@typing/Generated';
import { ContentConditionSource } from '@typing/Models';
import { handleMutationResponse } from '@utils/formUtils';
import { contentConditionKindsForSources } from '@utils/modelUtils';
import { optionsForRelationship, optionsForStage } from '@utils/optionUtils';

export type FormData = {
  careProfileField: CareProfileActionableFieldEnum | null;
  careProfileValue: CareProfileActionableFieldValueEnum | null;
  concern: CareProfileConcernEnum | null;
  id: string | undefined;
  kind: ContentConditionKindEnum | null;
  needFromGrayce: CareProfileNeedsFromGrayceEnum | null;
  providerEmotionalState: ClientRoleProviderEmotionalStateEnum | null;
  providerFeelsSupported: ClientRoleProviderFeelsSupportedEnum | null;
  relationship: ClientRoleRelationshipEnum | null;
  source: ContentConditionSource | null;
  stage: StageEnum | null;
};

type Props = {
  article?: ArticleType;
  goalTemplate?: GoalTemplateType;
  onCancel: () => void;
  onSuccess: (data?: FormData) => void;
};

const ContentConditionForm = ({ article, goalTemplate, onCancel, onSuccess }: Props) => {
  const intl = useIntl();

  const initialValues = {
    careProfileField: null,
    careProfileValue: null,
    concern: null,
    kind: null,
    needFromGrayce: null,
    providerEmotionalState: null,
    providerFeelsSupported: null,
    relationship: null,
    source: null
  };
  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const schema = yup
    .object()
    .shape({
      careProfileField: yup.string().when('kind', ([kind], schema) => {
        if (kind === ContentConditionKindEnum.care_profile_field_value) {
          return schema.required(intl.formatMessage({ id: 'errors.contentConditions.careProfileField.required' }));
        }
        return schema.nullable();
      }),
      careProfileValue: yup.string().when('kind', ([kind], schema) => {
        if (kind === ContentConditionKindEnum.care_profile_field_value) {
          return schema.required(intl.formatMessage({ id: 'errors.contentConditions.careProfileValue.required' }));
        }
        return schema.nullable();
      }),
      concern: yup.string().when('kind', ([kind], schema) => {
        if (kind === ContentConditionKindEnum.concern_selected) {
          return schema.required(intl.formatMessage({ id: 'errors.contentConditions.concern.required' }));
        }
        return schema.nullable();
      }),
      kind: yup.string().when('source', ([source], schema) => {
        if (
          source !== ContentConditionSource.PERSONALIZE_MY_EXPERIENCE &&
          source !== ContentConditionSource.INITIAL_CONSULT_REPORT &&
          source !== ContentConditionSource.ALWAYS
        ) {
          return schema.required(intl.formatMessage({ id: 'errors.contentConditions.kind.required' }));
        }
        return schema.nullable();
      }),
      needFromGrayce: yup.string().when('kind', ([kind], schema) => {
        if (kind === ContentConditionKindEnum.needs_from_grayce_selected) {
          return schema.required(intl.formatMessage({ id: 'errors.contentConditions.needFromGrayce.required' }));
        }
        return schema.nullable();
      }),
      providerEmotionalState: yup.string().when('kind', ([kind], schema) => {
        if (kind === ContentConditionKindEnum.provider_emotional_state_selected) {
          return schema.required(
            intl.formatMessage({ id: 'errors.contentConditions.providerEmotionalState.required' })
          );
        }
        return schema.nullable();
      }),
      providerFeelsSupported: yup.string().when('kind', ([kind], schema) => {
        if (kind === ContentConditionKindEnum.provider_feels_supported_selected) {
          return schema.required(
            intl.formatMessage({ id: 'errors.contentConditions.providerFeelsSupported.required' })
          );
        }
        return schema.nullable();
      }),
      relationship: yup.string().when('kind', ([kind], schema) => {
        if (kind === ContentConditionKindEnum.relationship_selected) {
          return schema.required(intl.formatMessage({ id: 'errors.contentConditions.relationship.required' }));
        }
        return schema.nullable();
      }),
      source: yup.string().required(intl.formatMessage({ id: 'errors.contentConditions.source.required' })),
      stage: yup.string().when('kind', ([kind], schema) => {
        if (kind === ContentConditionKindEnum.stage_selected) {
          return schema.required(intl.formatMessage({ id: 'errors.contentConditions.stage.required' }));
        }
        return schema.nullable();
      })
    })
    .required();

  const form = useForm<FormData>({
    defaultValues: initialValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const [createContentCondition, { client }] = useMutation<
    ContentConditionCreateMutation,
    ContentConditionCreateMutationVariables
  >(ContentConditionCreateDocument, {
    update: () => {
      // This sucks a little... we can update the article in the cache to include the new condition,
      // but there's also the query for the full condition list, and we cannot update that here.
      client.resetStore();
    }
  });

  const source = form.watch('source');
  const careProfileField = form.watch('careProfileField');
  const kind = form.watch('kind');

  const sourceOptions = Object.values(ContentConditionSource).map(option => ({
    label: intl.formatMessage({ id: `models.contentCondition.source.${option}` }),
    value: option
  }));

  const kindOptions = useMemo(
    () =>
      contentConditionKindsForSources([source]).map(option => ({
        label: intl.formatMessage({ id: `models.contentCondition.kind.${option}` }),
        value: option
      })),
    [source, intl]
  );

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);

    let useKind = kind;
    if (source === ContentConditionSource.ALWAYS) {
      useKind = ContentConditionKindEnum.always;
    }
    if (
      source === ContentConditionSource.PERSONALIZE_MY_EXPERIENCE ||
      source === ContentConditionSource.INITIAL_CONSULT_REPORT
    ) {
      useKind = ContentConditionKindEnum.care_profile_field_value;
    }

    if (useKind) {
      const contentData = {
        id: goalTemplate ? goalTemplate.id : article?.id,
        type: goalTemplate
          ? ConditionalContentTypeEnum.goal_template
          : article
          ? ConditionalContentTypeEnum.article
          : null
      };

      if (contentData.id && contentData.type) {
        return createContentCondition({
          variables: {
            ...form.getValues(),
            contentId: contentData.id,
            contentType: contentData.type,
            kind: useKind
          }
        }).then(response => {
          handleMutationResponse<FormData>({
            data: response.data?.contentConditionCreate,
            intl,
            onSuccess,
            setBaseErrors,
            setError: form.setError,
            txBase: 'errors.message'
          });
        });
      } else {
        onSuccess({ ...form.getValues(), kind: useKind });
      }
    }
  }, [article, createContentCondition, form, goalTemplate, intl, kind, onSuccess, source]);

  useEffect(() => {
    form.setValue('kind', null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source]);

  const concernOptions = useMemo(
    () =>
      Object.values(CareProfileConcernEnum).map(option => ({
        label: intl.formatMessage({ id: `models.careProfile.questions.concerns.options.${option}.content` }),
        value: option
      })),
    [intl]
  );

  const providerEmotionalStateOptions = useMemo(() => {
    const options = [
      ClientRoleProviderEmotionalStateEnum.very_negative,
      ClientRoleProviderEmotionalStateEnum.negative,
      ClientRoleProviderEmotionalStateEnum.neutral,
      ClientRoleProviderEmotionalStateEnum.positive,
      ClientRoleProviderEmotionalStateEnum.very_positive
    ];
    return options.map(option => ({
      label: intl.formatMessage({
        id: `models.pme.questions.provider_emotional_state.options.${option}.content`
      }),
      value: option
    }));
  }, [intl]);

  const providerFeelsSupportedOptions = useMemo(() => {
    const options = [
      ClientRoleProviderFeelsSupportedEnum.yes,
      ClientRoleProviderFeelsSupportedEnum.no,
      ClientRoleProviderFeelsSupportedEnum.its_complicated
    ];
    return options.map(option => ({
      label: intl.formatMessage({
        id: `models.pme.questions.provider_feels_supported.options.${option}.content`
      }),
      value: option
    }));
  }, [intl]);

  const relationshipOptions = optionsForRelationship(intl, 'models.careProfile.relationship');

  const stageOptions = optionsForStage(intl);

  const needsFromGrayceOptions = useMemo(() => {
    const options = [
      CareProfileNeedsFromGrayceEnum.planning,
      CareProfileNeedsFromGrayceEnum.complexity_of_support,
      CareProfileNeedsFromGrayceEnum.advice_or_resources,
      CareProfileNeedsFromGrayceEnum.navigating_transition,
      CareProfileNeedsFromGrayceEnum.crisis,
      CareProfileNeedsFromGrayceEnum.end_of_life,
      CareProfileNeedsFromGrayceEnum.fatigue,
      CareProfileNeedsFromGrayceEnum.just_browsing
    ];
    return options.map(option => ({
      label: intl.formatMessage({ id: `models.careProfile.needsFromGrayce.${option}` }),
      value: option
    }));
  }, [intl]);

  const careProfilePMEFieldOptions = useMemo(() => {
    const options = [
      CareProfileActionableFieldEnum.current_care_transitions,
      CareProfileActionableFieldEnum.help_level_required,
      CareProfileActionableFieldEnum.legal_help_needed,
      CareProfileActionableFieldEnum.medical_conditions,
      CareProfileActionableFieldEnum.provider_finances_impacted,
      CareProfileActionableFieldEnum.provider_financial_concerns,
      CareProfileActionableFieldEnum.residence_type,
      CareProfileActionableFieldEnum.sense_of_fulfillment,
      CareProfileActionableFieldEnum.social_satisfaction
    ];
    return options.map(option => ({
      label: intl.formatMessage({ id: `models.pme.questions.${option}.content` }),
      value: option
    }));
  }, [intl]);

  const careProfileICFieldOptions = useMemo(() => {
    const options = [
      CareProfileActionableFieldEnum.age,
      CareProfileActionableFieldEnum.caregiver_types,
      CareProfileActionableFieldEnum.dementia_severity,
      CareProfileActionableFieldEnum.emotional_concerns,
      CareProfileActionableFieldEnum.financial_manager,
      CareProfileActionableFieldEnum.health_insurance_types,
      CareProfileActionableFieldEnum.insurance_types,
      CareProfileActionableFieldEnum.marital_status,
      CareProfileActionableFieldEnum.recent_hospitalization,
      CareProfileActionableFieldEnum.religion,
      CareProfileActionableFieldEnum.va_connected,
      CareProfileActionableFieldEnum.veteran_connection
    ];
    return options.map(option => ({
      label: intl.formatMessage({ id: `models.initialConsultReport.questions.${option}.content` }),
      value: option
    }));
  }, [intl]);

  const careProfileICValueOptions = useMemo(() => {
    let options: string[] = [];

    if (careProfileField === CareProfileActionableFieldEnum.age) {
      options = options.concat([
        CareProfileActionableFieldValueEnum.sixty_five_and_older,
        CareProfileActionableFieldValueEnum.eighteen_to_sixty_four,
        CareProfileActionableFieldValueEnum.under_eighteen,
        CareProfileActionableFieldValueEnum.deceased
      ]);
    }
    if (careProfileField === CareProfileActionableFieldEnum.caregiver_types) {
      options = options.concat([
        CareProfileActionableFieldValueEnum.children,
        CareProfileActionableFieldValueEnum.spouse,
        CareProfileActionableFieldValueEnum.siblings,
        CareProfileActionableFieldValueEnum.friends,
        CareProfileActionableFieldValueEnum.caregivers,
        CareProfileActionableFieldValueEnum.neighbors,
        CareProfileActionableFieldValueEnum.other
      ]);
    }
    if (careProfileField === CareProfileActionableFieldEnum.dementia_severity) {
      options = options.concat([
        CareProfileActionableFieldValueEnum.mild,
        CareProfileActionableFieldValueEnum.moderate,
        CareProfileActionableFieldValueEnum.severe
      ]);
    }
    if (careProfileField === CareProfileActionableFieldEnum.emotional_concerns) {
      options = options.concat([
        CareProfileActionableFieldValueEnum.depression,
        CareProfileActionableFieldValueEnum.anxiety,
        CareProfileActionableFieldValueEnum.isolation,
        CareProfileActionableFieldValueEnum.weight_changes,
        CareProfileActionableFieldValueEnum.sleep_problems,
        CareProfileActionableFieldValueEnum.other,
        CareProfileActionableFieldValueEnum.none
      ]);
    }
    if (careProfileField === CareProfileActionableFieldEnum.financial_manager) {
      options = options.concat([
        CareProfileActionableFieldValueEnum.loved_one,
        CareProfileActionableFieldValueEnum.other,
        CareProfileActionableFieldValueEnum.fiduciary,
        CareProfileActionableFieldValueEnum.unsure
      ]);
    }
    if (careProfileField === CareProfileActionableFieldEnum.health_insurance_types) {
      options = options.concat([
        CareProfileActionableFieldValueEnum.medicare,
        CareProfileActionableFieldValueEnum.supplemental,
        CareProfileActionableFieldValueEnum.medicaid,
        CareProfileActionableFieldValueEnum.private,
        CareProfileActionableFieldValueEnum.veteran,
        CareProfileActionableFieldValueEnum.other,
        CareProfileActionableFieldValueEnum.none
      ]);
    }
    if (careProfileField === CareProfileActionableFieldEnum.insurance_types) {
      options = options.concat([
        CareProfileActionableFieldValueEnum.life_insurance,
        CareProfileActionableFieldValueEnum.long_term_care_insurance,
        CareProfileActionableFieldValueEnum.other,
        CareProfileActionableFieldValueEnum.none
      ]);
    }
    if (careProfileField === CareProfileActionableFieldEnum.marital_status) {
      options = options.concat([
        CareProfileActionableFieldValueEnum.single,
        CareProfileActionableFieldValueEnum.married,
        CareProfileActionableFieldValueEnum.domestic_partner,
        CareProfileActionableFieldValueEnum.divorced,
        CareProfileActionableFieldValueEnum.separated,
        CareProfileActionableFieldValueEnum.widowed,
        CareProfileActionableFieldValueEnum.other
      ]);
    }
    if (careProfileField === CareProfileActionableFieldEnum.recent_hospitalization) {
      options = options.concat([CareProfileActionableFieldValueEnum.yes, CareProfileActionableFieldValueEnum.no]);
    }
    if (careProfileField === CareProfileActionableFieldEnum.religion) {
      options = options.concat([
        CareProfileActionableFieldValueEnum.none,
        CareProfileActionableFieldValueEnum.atheism,
        CareProfileActionableFieldValueEnum.bahai,
        CareProfileActionableFieldValueEnum.buddhism,
        CareProfileActionableFieldValueEnum.catholicism,
        CareProfileActionableFieldValueEnum.christianity,
        CareProfileActionableFieldValueEnum.hinduism,
        CareProfileActionableFieldValueEnum.islam,
        CareProfileActionableFieldValueEnum.jainism,
        CareProfileActionableFieldValueEnum.jehovahs_witness,
        CareProfileActionableFieldValueEnum.judaism,
        CareProfileActionableFieldValueEnum.mormonism,
        CareProfileActionableFieldValueEnum.other,
        CareProfileActionableFieldValueEnum.paganism,
        CareProfileActionableFieldValueEnum.rastafari,
        CareProfileActionableFieldValueEnum.santeria,
        CareProfileActionableFieldValueEnum.shinto,
        CareProfileActionableFieldValueEnum.sikhism,
        CareProfileActionableFieldValueEnum.spiritualism,
        CareProfileActionableFieldValueEnum.taoism,
        CareProfileActionableFieldValueEnum.unitarianism,
        CareProfileActionableFieldValueEnum.zoroastrianism
      ]);
    }
    if (careProfileField === CareProfileActionableFieldEnum.va_connected) {
      options = options.concat([CareProfileActionableFieldValueEnum.yes, CareProfileActionableFieldValueEnum.no]);
    }
    if (careProfileField === CareProfileActionableFieldEnum.veteran_connection) {
      options = options.concat([
        CareProfileActionableFieldValueEnum.self,
        CareProfileActionableFieldValueEnum.spouse,
        CareProfileActionableFieldValueEnum.neither
      ]);
    }
    if (careProfileField) {
      return options.map(option => ({
        label: intl.formatMessage({
          id: `models.initialConsultReport.questions.${careProfileField}.options.${option}.content`
        }),
        value: option
      }));
    }
  }, [careProfileField, intl]);

  const careProfilePMEValueOptions = useMemo(() => {
    let options: string[] = [];

    if (careProfileField === CareProfileActionableFieldEnum.current_care_transitions) {
      options = options.concat([
        CareProfileActionableFieldValueEnum.adding_part_time_care,
        CareProfileActionableFieldValueEnum.adding_full_time_care,
        CareProfileActionableFieldValueEnum.reducing_care,
        CareProfileActionableFieldValueEnum.moving,
        CareProfileActionableFieldValueEnum.transferring,
        CareProfileActionableFieldValueEnum.going_home,
        CareProfileActionableFieldValueEnum.none,
        CareProfileActionableFieldValueEnum.other
      ]);
    } else if (careProfileField === CareProfileActionableFieldEnum.help_level_required) {
      options = options.concat([
        CareProfileActionableFieldValueEnum.none,
        CareProfileActionableFieldValueEnum.basic_support,
        CareProfileActionableFieldValueEnum.constant_care,
        CareProfileActionableFieldValueEnum.crisis,
        CareProfileActionableFieldValueEnum.end_of_life,
        CareProfileActionableFieldValueEnum.deceased
      ]);
    } else if (careProfileField === CareProfileActionableFieldEnum.legal_help_needed) {
      options = options.concat([CareProfileActionableFieldValueEnum.yes, CareProfileActionableFieldValueEnum.no]);
    } else if (careProfileField === CareProfileActionableFieldEnum.medical_conditions) {
      options = options.concat([
        CareProfileActionableFieldValueEnum.heart_condition,
        CareProfileActionableFieldValueEnum.cancer,
        CareProfileActionableFieldValueEnum.lung_condition,
        CareProfileActionableFieldValueEnum.stroke,
        CareProfileActionableFieldValueEnum.diabetes,
        CareProfileActionableFieldValueEnum.dementia,
        CareProfileActionableFieldValueEnum.kidney_condition,
        CareProfileActionableFieldValueEnum.hearing_or_vision_loss,
        CareProfileActionableFieldValueEnum.mental_health,
        CareProfileActionableFieldValueEnum.im_not_sure,
        CareProfileActionableFieldValueEnum.none,
        CareProfileActionableFieldValueEnum.other
      ]);
    } else if (careProfileField === CareProfileActionableFieldEnum.provider_finances_impacted) {
      options = options.concat([CareProfileActionableFieldValueEnum.yes, CareProfileActionableFieldValueEnum.no]);
    } else if (careProfileField === CareProfileActionableFieldEnum.provider_financial_concerns) {
      options = options.concat([
        CareProfileActionableFieldValueEnum.qualifying_for_financial_programs,
        CareProfileActionableFieldValueEnum.paying_for_care,
        CareProfileActionableFieldValueEnum.managing_finances,
        CareProfileActionableFieldValueEnum.planning_for_future,
        CareProfileActionableFieldValueEnum.other,
        CareProfileActionableFieldValueEnum.no
      ]);
    } else if (careProfileField === CareProfileActionableFieldEnum.residence_type) {
      options = options.concat([
        CareProfileActionableFieldValueEnum.home_alone,
        CareProfileActionableFieldValueEnum.home_with_others,
        CareProfileActionableFieldValueEnum.assisted_living_community,
        CareProfileActionableFieldValueEnum.memory_care_community,
        CareProfileActionableFieldValueEnum.skilled_nursing_facility,
        CareProfileActionableFieldValueEnum.other
      ]);
    } else if (careProfileField === CareProfileActionableFieldEnum.sense_of_fulfillment) {
      options = options.concat([
        CareProfileActionableFieldValueEnum.not_at_all,
        CareProfileActionableFieldValueEnum.level_2,
        CareProfileActionableFieldValueEnum.neutral,
        CareProfileActionableFieldValueEnum.level_4,
        CareProfileActionableFieldValueEnum.extremely
      ]);
    } else if (careProfileField === CareProfileActionableFieldEnum.social_satisfaction) {
      options = options.concat([
        CareProfileActionableFieldValueEnum.not_at_all,
        CareProfileActionableFieldValueEnum.level_2,
        CareProfileActionableFieldValueEnum.neutral,
        CareProfileActionableFieldValueEnum.level_4,
        CareProfileActionableFieldValueEnum.extremely
      ]);
    }

    if (careProfileField) {
      return options.map(option => ({
        label: intl.formatMessage({ id: `models.pme.questions.${careProfileField}.options.${option}.content` }),
        value: option
      }));
    }
    return [];
  }, [intl, careProfileField]);

  return (
    <Form hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />
      <div>
        <Select<FormData>
          control={form.control}
          error={form.formState.errors.source}
          label={intl.formatMessage({ id: 'forms.contentConditions.source' })}
          name="source"
          options={sourceOptions}
        />
      </div>
      <div
        className={
          source === ContentConditionSource.PERSONALIZE_MY_EXPERIENCE ||
          source === ContentConditionSource.INITIAL_CONSULT_REPORT
            ? undefined
            : 'ion-hide'
        }
      >
        <Select<FormData>
          control={form.control}
          error={form.formState.errors.careProfileField}
          label={intl.formatMessage({ id: 'forms.contentConditions.question' })}
          name="careProfileField"
          options={
            source === ContentConditionSource.PERSONALIZE_MY_EXPERIENCE
              ? careProfilePMEFieldOptions
              : careProfileICFieldOptions ?? []
          }
        />
        <div className={careProfilePMEValueOptions ? undefined : 'ion-hide'}>
          <Select<FormData>
            control={form.control}
            error={form.formState.errors.careProfileValue}
            label={intl.formatMessage({ id: 'forms.contentConditions.answer' })}
            name="careProfileValue"
            options={
              source === ContentConditionSource.PERSONALIZE_MY_EXPERIENCE
                ? careProfilePMEValueOptions
                : careProfileICValueOptions ?? []
            }
          />
        </div>
      </div>
      <div
        className={
          source &&
          source !== ContentConditionSource.PERSONALIZE_MY_EXPERIENCE &&
          source !== ContentConditionSource.ALWAYS &&
          source !== ContentConditionSource.INITIAL_CONSULT_REPORT
            ? undefined
            : 'ion-hide'
        }
      >
        <Select<FormData>
          control={form.control}
          error={form.formState.errors.kind}
          label={intl.formatMessage({ id: 'forms.contentConditions.criteria' })}
          name="kind"
          options={kindOptions}
        />
      </div>
      <div className={kind === ContentConditionKindEnum.concern_selected ? undefined : 'ion-hide'}>
        <Select<FormData>
          control={form.control}
          error={form.formState.errors.concern}
          label={intl.formatMessage({ id: 'forms.contentConditions.concern' })}
          name="concern"
          options={concernOptions}
        />
      </div>
      <div className={kind === ContentConditionKindEnum.stage_selected ? undefined : 'ion-hide'}>
        <Select<FormData>
          control={form.control}
          error={form.formState.errors.stage}
          label={intl.formatMessage({ id: 'forms.contentConditions.stage' })}
          name="stage"
          options={stageOptions}
        />
      </div>
      <div className={kind === ContentConditionKindEnum.needs_from_grayce_selected ? undefined : 'ion-hide'}>
        <Select<FormData>
          control={form.control}
          error={form.formState.errors.needFromGrayce}
          label={intl.formatMessage({ id: 'forms.contentConditions.needFromGrayce' })}
          name="needFromGrayce"
          options={needsFromGrayceOptions}
        />
      </div>
      <div className={kind === ContentConditionKindEnum.provider_emotional_state_selected ? undefined : 'ion-hide'}>
        <Select<FormData>
          control={form.control}
          error={form.formState.errors.providerEmotionalState}
          label={intl.formatMessage({ id: 'forms.contentConditions.providerEmotionalState' })}
          name="providerEmotionalState"
          options={providerEmotionalStateOptions}
        />
      </div>
      <div className={kind === ContentConditionKindEnum.provider_feels_supported_selected ? undefined : 'ion-hide'}>
        <Select<FormData>
          control={form.control}
          error={form.formState.errors.providerFeelsSupported}
          label={intl.formatMessage({ id: 'forms.contentConditions.providerFeelsSupported' })}
          name="providerFeelsSupported"
          options={providerFeelsSupportedOptions}
        />
      </div>
      <div className={kind === ContentConditionKindEnum.relationship_selected ? undefined : 'ion-hide'}>
        <Select<FormData>
          control={form.control}
          error={form.formState.errors.relationship}
          label={intl.formatMessage({ id: 'forms.contentConditions.relationship' })}
          name="relationship"
          options={relationshipOptions}
        />
      </div>

      <FormButtons hookForm={form} onCancel={onCancel} saveKey="dictionary.save" />
    </Form>
  );
};

export default ContentConditionForm;
